import { isObject } from 'lodash';
import isArrayShallowEqual from 'utils/is-array-shallow-equal';

/**
 * Performs a shallow comparison of two objects (1-level deep).
 * Typically to be used within React's shouldComponentUpdate for quick re-rendering checks
 * between this.props and nextProps.
 *
 * @param {Object} obj1 - the first object to compare.
 * @param {Object} obj2 - the second object to compare.
 * @returns {Boolean} - true if both objects are shallow equal, false if not.
 **/
const isObjectShallowEqual = (obj1, obj2) => {
  if (!isObject(obj1) || !isObject(obj2)) {
    return false;
  }

  // are the references the same?
  if (obj1 === obj2) {
    return true;
  }

  // does it contain objects with the same keys?
  const item1Keys = Object.keys(obj1).sort();
  const item2Keys = Object.keys(obj2).sort();

  if (!isArrayShallowEqual(item1Keys, item2Keys)) {
    return false;
  }

  // does every value in the object have the same reference?
  return item2Keys.every(key => {
    const value = obj1[key];
    const nextValue = obj2[key];

    if (value === nextValue) {
      return true;
    }

    // special case for arrays - check one level deep
    return (
      Array.isArray(value) &&
      Array.isArray(nextValue) &&
      isArrayShallowEqual(value, nextValue)
    );
  });
};

export default isObjectShallowEqual;
export { isObjectShallowEqual };
